import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Loader2 } from 'lucide-react';
import {
  Buildings, ChatCircleText, CalendarCheck, Download, ClipboardText,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { downloadBlob } from '../../utils/downloadBlob';
import { fetchPdfBlob } from '../../utils/fetchPdfBlob';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

/**
 * Read-only preview of the Rapport Mensuel v2 :
 *  - Stats (CRs, absences, réserves, tâches)
 *  - Bilan des absences par entreprise
 *  - Décisions et actions majeures
 *  - Bouton "Télécharger le PDF"
 */
export function MonthlyPreviewSheet({ open, onOpenChange, chantierId, chantierName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const now = useMemo(() => new Date(), []);
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  const monthLabel = `${MONTHS_FR[month - 1]} ${year}`;

  const load = useCallback(async () => {
    if (!chantierId) return;
    setLoading(true);
    setData(null);
    try {
      const res = await axios.post(`${API}/reports/monthly/prepare`,
        { chantier_id: chantierId, month, year },
        { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur préparation aperçu');
      onOpenChange(false);
    } finally { setLoading(false); }
  }, [chantierId, month, year, onOpenChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetchPdfBlob('documents/rapport-mensuel/auto', chantierId);
      downloadBlob(res, 'Rapport_Mensuel.pdf');
      toast.success('Rapport mensuel téléchargé');
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Erreur téléchargement PDF');
    } finally { setDownloading(false); }
  };

  const stats = data?.stats || {};
  const raw = data?.raw_data || {};
  const absences = raw.absences || [];
  const meetings = raw.meeting_reports || [];
  const decisions = useMemo(() => {
    const out = [];
    for (const mr of meetings) {
      for (const ag of (mr.agenda_items || [])) {
        const dec = (ag.decision || '').trim();
        if (!dec) continue;
        out.push({
          date: mr.meeting_date || '',
          topic: (ag.topic || '').trim(),
          decision: dec,
          responsable: (ag.responsable || '').trim(),
          deadline: (ag.deadline || '').trim(),
        });
      }
    }
    return out;
  }, [meetings]);

  const totalConvocs = absences.reduce((s, r) => s + (r.total_meetings || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto"
        data-testid="monthly-preview-sheet"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarCheck size={22} className="text-indigo-600" />
            Aperçu Rapport Mensuel — {monthLabel}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#4B5563] mt-1">
            {chantierName || 'Chantier'} — Vérifiez les éléments qui apparaîtront dans le PDF
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : data ? (
          <div className="space-y-5 mt-2">
            {/* Stats bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="preview-stats">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-orange-700">{stats.meeting_reports_count ?? 0}</p>
                <p className="text-xs text-orange-600">Comptes rendus</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-red-700">{stats.absences_total ?? 0}</p>
                <p className="text-xs text-red-600">Absences ({stats.excused_total ?? 0} excusés)</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-700">{stats.reserves_total ?? 0}</p>
                <p className="text-xs text-amber-600">Réserves ({stats.reserves_open ?? 0} ouv.)</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-emerald-700">{stats.tasks_completed ?? 0}</p>
                <p className="text-xs text-emerald-600">Tâches / {stats.tasks_total ?? 0}</p>
              </div>
            </div>

            {/* Meetings list */}
            <section data-testid="preview-meetings">
              <div className="flex items-center gap-2 mb-2">
                <CalendarCheck size={18} className="text-[#0055FF]" />
                <h3 className="font-semibold text-[#111827]">Comptes rendus du mois</h3>
              </div>
              {meetings.length === 0 ? (
                <p className="text-sm text-[#6B7280] italic px-3 py-2 bg-gray-50 rounded" data-testid="meetings-empty">
                  Aucun compte rendu saisi pour {monthLabel.toLowerCase()}.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {meetings.map((m) => (
                    <li key={m.id} className="text-sm text-[#111827] px-3 py-2 bg-gray-50 rounded flex items-center justify-between flex-wrap gap-2"
                      data-testid={`meeting-item-${m.id}`}>
                      <span>
                        <strong>{m.meeting_date}</strong>
                        {m.meeting_time ? ` à ${m.meeting_time}` : ''}
                        {m.meeting_number ? ` • Réunion n°${m.meeting_number}` : ''}
                        {m.title ? ` — ${m.title}` : ''}
                      </span>
                      <span className="text-xs text-[#4B5563]">
                        {(m.attendees || []).length} participant(s) • {(m.agenda_items || []).length} point(s)
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Absences table */}
            <section data-testid="preview-absences-section">
              <div className="flex items-center gap-2 mb-2">
                <Buildings size={18} className="text-red-600" />
                <h3 className="font-semibold text-[#111827]">Bilan des absences par entreprise</h3>
              </div>
              {absences.length === 0 ? (
                <p className="text-sm text-[#6B7280] italic px-3 py-2 bg-gray-50 rounded" data-testid="absences-empty">
                  Aucune présence enregistrée ce mois-ci.
                </p>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[#4B5563] text-xs uppercase">
                      <tr>
                        <th className="px-3 py-2 text-left">Entreprise</th>
                        <th className="px-3 py-2 text-left">Lot</th>
                        <th className="px-3 py-2 text-center">Convocations</th>
                        <th className="px-3 py-2 text-center">Absences</th>
                        <th className="px-3 py-2 text-center">Excusés</th>
                      </tr>
                    </thead>
                    <tbody>
                      {absences.map((row, idx) => (
                        <tr key={`abs-${idx}`} className="border-t border-gray-100" data-testid={`abs-row-${idx}`}>
                          <td className="px-3 py-2 font-medium text-[#111827]">{row.entreprise_name || '-'}</td>
                          <td className="px-3 py-2 text-[#4B5563]">{row.lot_name || '-'}</td>
                          <td className="px-3 py-2 text-center text-[#4B5563]">{row.total_meetings}</td>
                          <td className={`px-3 py-2 text-center font-semibold ${row.absent_count > 0 ? 'text-red-600' : 'text-[#111827]'}`}>{row.absent_count}</td>
                          <td className={`px-3 py-2 text-center font-semibold ${row.excused_count > 0 ? 'text-amber-600' : 'text-[#111827]'}`}>{row.excused_count}</td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 border-t border-gray-200 font-semibold" data-testid="abs-total-row">
                        <td className="px-3 py-2" colSpan={2}>TOTAL</td>
                        <td className="px-3 py-2 text-center">{totalConvocs}</td>
                        <td className="px-3 py-2 text-center text-red-600">{stats.absences_total ?? 0}</td>
                        <td className="px-3 py-2 text-center text-amber-600">{stats.excused_total ?? 0}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Decisions */}
            <section data-testid="preview-decisions-section">
              <div className="flex items-center gap-2 mb-2">
                <ChatCircleText size={18} className="text-[#FF6B00]" />
                <h3 className="font-semibold text-[#111827]">
                  Décisions et actions majeures ({decisions.length})
                </h3>
              </div>
              {decisions.length === 0 ? (
                <p className="text-sm text-[#6B7280] italic px-3 py-2 bg-gray-50 rounded" data-testid="decisions-empty">
                  Aucune décision consignée dans les comptes rendus.
                </p>
              ) : (
                <ul className="space-y-2">
                  {decisions.slice(0, 12).map((d, i) => (
                    <li key={`dec-${i}`} className="border-l-4 border-orange-500 pl-3 py-1" data-testid={`dec-row-${i}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-[#0055FF]">{d.date}</span>
                        {d.topic && <span className="text-xs text-[#4B5563]">— {d.topic}</span>}
                      </div>
                      <p className="text-sm text-[#111827] mt-0.5 whitespace-pre-wrap">{d.decision}</p>
                      {(d.responsable || d.deadline) && (
                        <div className="mt-0.5 flex items-center gap-3 text-xs text-[#6B7280]">
                          {d.responsable && <span>Resp. : <strong className="text-[#111827]">{d.responsable}</strong></span>}
                          {d.deadline && <span>Éch. : <strong className="text-[#111827]">{d.deadline}</strong></span>}
                        </div>
                      )}
                    </li>
                  ))}
                  {decisions.length > 12 && (
                    <li className="text-xs text-[#6B7280] italic pl-3">
                      ... +{decisions.length - 12} décision(s) supplémentaire(s)
                    </li>
                  )}
                </ul>
              )}
            </section>

            {/* Reserves count */}
            <section className="flex items-center gap-2 text-sm text-[#4B5563]" data-testid="preview-reserves-line">
              <ClipboardText size={16} className="text-amber-600" />
              <span>
                Réserves : <strong>{stats.reserves_total ?? 0}</strong> total —
                {' '}{stats.reserves_open ?? 0} ouvertes / {stats.reserves_closed ?? 0} levées
              </span>
            </section>
          </div>
        ) : (
          <div className="py-8 text-center text-[#6B7280] text-sm">Aucune donnée</div>
        )}

        {/* Footer CTA */}
        <div className="flex gap-3 pt-4 border-t border-gray-100 mt-2 sticky bottom-0 bg-white">
          <Button variant="outline" className="flex-1 h-12" onClick={() => onOpenChange(false)}
            data-testid="preview-close-btn">
            Fermer
          </Button>
          <Button onClick={handleDownload} disabled={downloading || loading || !data}
            className="flex-1 h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
            data-testid="preview-download-btn">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download size={18} className="mr-2" />}
            {downloading ? 'Téléchargement...' : 'Télécharger le PDF'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
